import React, { useRef, useState } from "react";
import { Upload, X, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

export default function ImageUploader({ images, onChange }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const upload = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    for (let idx = 0; idx < list.length; idx++) {
      const file = list[idx];
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} is over 10 MB`); continue; }
      const form = new FormData();
      form.append("file", file);
      try {
        setProgress({ name: file.name, pct: 0, n: idx + 1, of: list.length });
        const { data } = await api.post("/admin/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setProgress((p) => ({ ...p, pct: e.total ? Math.round((e.loaded / e.total) * 100) : 50 })),
        });
        onChange([...images, data.url]);
        images = [...images, data.url];
        toast.success(`${file.name} uploaded`);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `Upload failed for ${file.name}`);
      }
    }
    setProgress(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (i) => onChange(images.filter((_, k) => k !== i));
  const makeMain = (i) => onChange([images[i], ...images.filter((_, k) => k !== i)]);

  return (
    <div className="sm:col-span-2 space-y-3" data-testid="image-uploader">
      <p className="eyebrow text-[0.55rem]">Product photos · first photo is the main image</p>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        data-testid="image-dropzone"
        className="border border-dashed border-[rgba(212,175,55,0.4)] rounded-xl p-5 text-center cursor-pointer hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-colors">
        <Upload size={20} className="mx-auto text-[#D4AF37] mb-2" />
        <p className="text-sm text-[#FAFAF7]/80">Click to upload or drag & drop</p>
        <p className="text-xs text-[#FAFAF7]/40 mt-1">JPG, PNG, WEBP · up to 10 MB each · multiple allowed</p>
        <input ref={inputRef} data-testid="image-file-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
      </div>
      {progress && (
        <div data-testid="upload-progress">
          <div className="flex justify-between text-xs text-[#FAFAF7]/60 mb-1"><span>Uploading {progress.name} ({progress.n}/{progress.of})</span><span>{progress.pct}%</span></div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-[#D4AF37] transition-[width]" style={{ width: `${progress.pct}%` }} /></div>
        </div>
      )}
      {images.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          {images.map((src, i) => (
            <div key={src + i} className="relative group" data-testid={`image-thumb-${i}`}>
              <img src={src} alt="" className={`w-20 h-24 object-cover rounded-lg ${i === 0 ? "ring-2 ring-[#D4AF37]" : "gold-border"}`} />
              {i === 0 && <span className="absolute -top-1.5 -left-1.5 bg-[#D4AF37] text-[#0B0B0C] rounded-full p-0.5"><Star size={10} className="fill-current" /></span>}
              <button type="button" data-testid={`image-remove-${i}`} onClick={() => remove(i)} aria-label="Remove image"
                className="absolute -top-1.5 -right-1.5 bg-[#0B0B0C] gold-border rounded-full p-0.5 text-[#FAFAF7]/80 hover:text-[#58111A]"><X size={12} /></button>
              {i > 0 && <button type="button" data-testid={`image-main-${i}`} onClick={() => makeMain(i)} className="absolute bottom-1 left-1 right-1 text-[0.6rem] bg-[#0B0B0C]/85 rounded px-1 py-0.5 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity">Make main</button>}
            </div>
          ))}
        </div>
      )}
      <input data-testid="pm-images" placeholder="…or paste image URLs (comma separated)" value={images.join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className="input-lux w-full text-xs" />
    </div>
  );
}
